import { useEffect, useState } from "react";
import { Plus, Pencil, Trash2, Upload, Loader2, X } from "lucide-react";
import api, { formatApiErrorDetail } from "../../lib/api";
import { toast } from "../../components/ui/sonner";
import { CATEGORIES } from "../../lib/constants";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { Label } from "../../components/ui/label";
import { Badge } from "../../components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../../components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "../../components/ui/alert-dialog";

const emptyForm = { sku: "", name: "", category: CATEGORIES[0], description: "", price: "", stock_status: "in_stock", stock_quantity: "0", low_stock_threshold: "5", compatibility: "", image: "", images: [], featured: false };

export function PartsManager({ onChange, focusSku, onFocusHandled }) {
  const [parts, setParts] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [uploading, setUploading] = useState(false);
  const [galleryUploading, setGalleryUploading] = useState(false);

  const uploadFile = async (file) => {
    const fd = new FormData();
    fd.append("file", file);
    const res = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
    return `${process.env.REACT_APP_BACKEND_URL}${res.data.url}`;
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true);
    try {
      const url = await uploadFile(file);
      setForm((f) => ({ ...f, image: url }));
      toast.success("Photo uploaded");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Upload failed");
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const handleGalleryUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setGalleryUploading(true);
    try {
      const urls = await Promise.all(files.map(uploadFile));
      setForm((f) => ({ ...f, images: [...f.images, ...urls] }));
      toast.success(`${urls.length} angle shot${urls.length > 1 ? "s" : ""} uploaded`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Upload failed");
    } finally {
      setGalleryUploading(false);
      e.target.value = "";
    }
  };

  const removeGalleryImage = (idx) => {
    setForm((f) => ({ ...f, images: f.images.filter((_, i) => i !== idx) }));
  };

  const load = () => api.get("/parts").then((res) => setParts(res.data));
  useEffect(() => { load(); }, []);

  const openAdd = () => { setEditing(null); setForm(emptyForm); setOpen(true); };
  const openEdit = (p) => { setEditing(p); setForm({ ...emptyForm, ...p, price: String(p.price), stock_quantity: String(p.stock_quantity ?? 0), low_stock_threshold: String(p.low_stock_threshold ?? 5), images: p.images || [] }); setOpen(true); };

  useEffect(() => {
    if (!focusSku) return;
    api
      .get(`/parts/${focusSku}`)
      .then((res) => openEdit(res.data))
      .catch(() => toast.error(`Could not load ${focusSku}`))
      .finally(() => onFocusHandled?.());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [focusSku]);

  const handleSave = async () => {
    try {
      const payload = { ...form, price: parseFloat(form.price), stock_quantity: parseInt(form.stock_quantity, 10) || 0, low_stock_threshold: parseInt(form.low_stock_threshold, 10) || 5 };
      if (editing) {
        await api.put(`/parts/${editing.id}`, payload);
        toast.success("Part updated");
      } else {
        await api.post("/parts", payload);
        toast.success("Part added");
      }
      setOpen(false);
      load();
      onChange?.();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Save failed");
    }
  };

  const handleDelete = async (id) => {
    try {
      await api.delete(`/parts/${id}`);
      toast.success("Part deleted");
      load();
      onChange?.();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Delete failed");
    }
  };

  return (
    <div data-testid="admin-parts-manager">
      <div className="flex justify-end mb-4">
        <Button onClick={openAdd} data-testid="admin-add-part-btn" className="bg-amber-light text-obsidian hover:bg-white">
          <Plus className="h-4 w-4 mr-2" /> Add Part
        </Button>
      </div>

      <div className="border border-amber-500/10 rounded-md overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="border-amber-500/10">
              <TableHead>SKU</TableHead>
              <TableHead>Name</TableHead>
              <TableHead>Category</TableHead>
              <TableHead>Price</TableHead>
              <TableHead>Stock</TableHead>
              <TableHead>Qty</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {parts.map((p) => (
              <TableRow key={p.id} className="border-amber-500/10" data-testid={`admin-part-row-${p.sku}`}>
                <TableCell className="font-mono text-xs text-amber-light">{p.sku}</TableCell>
                <TableCell className="text-white">{p.name}</TableCell>
                <TableCell className="text-zinc-400">{p.category}</TableCell>
                <TableCell className="text-zinc-300">${p.price.toLocaleString()}</TableCell>
                <TableCell className="space-x-1.5">
                  <Badge variant={p.stock_status === "in_stock" ? "default" : "destructive"}>
                    {p.stock_status === "in_stock" ? "In Stock" : "Out of Stock"}
                  </Badge>
                  {p.stock_quantity > 0 && p.stock_quantity <= p.low_stock_threshold && (
                    <Badge data-testid={`admin-low-stock-badge-${p.sku}`} className="bg-amber-900/60 text-amber-300 border border-amber-500/40 hover:bg-amber-900/60">
                      Low Stock
                    </Badge>
                  )}
                </TableCell>
                <TableCell className="text-zinc-400 font-mono text-xs" data-testid={`admin-part-qty-${p.sku}`}>{p.stock_quantity ?? 0}</TableCell>
                <TableCell className="text-right space-x-2">
                  <Button size="icon" variant="outline" onClick={() => openEdit(p)} data-testid={`admin-edit-part-btn-${p.sku}`}>
                    <Pencil className="h-3.5 w-3.5" />
                  </Button>
                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <Button size="icon" variant="destructive" data-testid={`admin-delete-part-btn-${p.sku}`}>
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    </AlertDialogTrigger>
                    <AlertDialogContent>
                      <AlertDialogHeader>
                        <AlertDialogTitle>Delete {p.sku}?</AlertDialogTitle>
                        <AlertDialogDescription>This will permanently remove this part from the catalog.</AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel>Cancel</AlertDialogCancel>
                        <AlertDialogAction onClick={() => handleDelete(p.id)} data-testid={`admin-confirm-delete-btn-${p.sku}`}>Delete</AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl !bg-carbon border-amber-500/15 max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-white">{editing ? "Edit Part" : "Add New Part"}</DialogTitle>
            <DialogDescription>Fill in the part details, pricing, stock levels, and photos.</DialogDescription>
          </DialogHeader>
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <Label className="text-xs text-zinc-400">SKU</Label>
              <Input data-testid="admin-part-form-sku" value={form.sku} onChange={(e) => setForm({ ...form, sku: e.target.value })} />
            </div>
            <div>
              <Label className="text-xs text-zinc-400">Name</Label>
              <Input data-testid="admin-part-form-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
            </div>
            <div>
              <Label className="text-xs text-zinc-400">Category</Label>
              <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                <SelectTrigger data-testid="admin-part-form-category"><SelectValue /></SelectTrigger>
                <SelectContent>{CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs text-zinc-400">Price (USD)</Label>
              <Input data-testid="admin-part-form-price" type="number" step="0.01" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} />
            </div>
            <div>
              <Label className="text-xs text-zinc-400">Stock Status</Label>
              <Select value={form.stock_status} onValueChange={(v) => setForm({ ...form, stock_status: v })}>
                <SelectTrigger data-testid="admin-part-form-stock"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="in_stock">In Stock</SelectItem>
                  <SelectItem value="out_of_stock">Out of Stock</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs text-zinc-400">Stock Quantity</Label>
              <Input data-testid="admin-part-form-quantity" type="number" min="0" value={form.stock_quantity} onChange={(e) => setForm({ ...form, stock_quantity: e.target.value })} />
            </div>
            <div>
              <Label className="text-xs text-zinc-400">Low Stock Alert Threshold</Label>
              <Input data-testid="admin-part-form-threshold" type="number" min="0" value={form.low_stock_threshold} onChange={(e) => setForm({ ...form, low_stock_threshold: e.target.value })} />
            </div>
            <div>
              <Label className="text-xs text-zinc-400">Cover Photo</Label>
              <div className="flex items-center gap-3 mt-1">
                {form.image ? (
                  <img src={form.image} alt="Part preview" className="h-14 w-14 rounded object-cover border border-amber-500/20" data-testid="admin-part-form-image-preview" />
                ) : (
                  <div className="h-14 w-14 rounded border border-dashed border-amber-500/20 flex items-center justify-center text-zinc-600">
                    <Upload className="h-4 w-4" />
                  </div>
                )}
                <label className="flex items-center gap-2 text-xs font-mono uppercase text-zinc-300 border border-amber-500/20 rounded px-3 py-2 cursor-pointer hover:border-amber-500/50 hover:text-amber-light">
                  {uploading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Upload className="h-3.5 w-3.5" />}
                  {uploading ? "Uploading..." : "Upload Photo"}
                  <input type="file" accept="image/*" data-testid="admin-part-form-image-upload" onChange={handleImageUpload} disabled={uploading} className="hidden" />
                </label>
              </div>
            </div>
            <div className="sm:col-span-2">
              <Label className="text-xs text-zinc-400">Additional Angle Shots</Label>
              <div className="flex flex-wrap items-center gap-3 mt-1">
                {form.images.map((img, idx) => (
                  <div key={idx} className="relative" data-testid={`admin-part-form-gallery-thumb-${idx}`}>
                    <img src={img} alt={`Angle ${idx + 1}`} className="h-14 w-14 rounded object-cover border border-amber-500/20" />
                    <button
                      type="button"
                      onClick={() => removeGalleryImage(idx)}
                      data-testid={`admin-part-form-gallery-remove-${idx}`}
                      className="absolute -top-1.5 -right-1.5 h-5 w-5 rounded-full bg-rose-600 text-white text-xs flex items-center justify-center hover:bg-rose-500"
                    >
                      <X className="h-3 w-3" />
                    </button>
                  </div>
                ))}
                <label className="flex items-center gap-2 text-xs font-mono uppercase text-zinc-300 border border-amber-500/20 rounded px-3 py-2 cursor-pointer hover:border-amber-500/50 hover:text-amber-light">
                  {galleryUploading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Upload className="h-3.5 w-3.5" />}
                  {galleryUploading ? "Uploading..." : "Add Photos"}
                  <input type="file" accept="image/*" multiple data-testid="admin-part-form-gallery-upload" onChange={handleGalleryUpload} disabled={galleryUploading} className="hidden" />
                </label>
              </div>
            </div>
            <div className="sm:col-span-2">
              <Label className="text-xs text-zinc-400">Compatibility</Label>
              <Input data-testid="admin-part-form-compatibility" value={form.compatibility} onChange={(e) => setForm({ ...form, compatibility: e.target.value })} />
            </div>
            <div className="sm:col-span-2">
              <Label className="text-xs text-zinc-400">Description</Label>
              <Textarea data-testid="admin-part-form-description" rows={3} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
            </div>
          </div>
          <DialogFooter>
            <Button onClick={handleSave} data-testid="admin-save-part-btn" className="bg-amber-light text-obsidian hover:bg-white">
              {editing ? "Save Changes" : "Add Part"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
